import { coreApiRootUrl, apiRootUrl } from '../config.local';
import AppApi from './AppApi';
import Customer, {
	CreateCustomerCheckCategoryRequest,
	CustomerCheckCategory,
	Registration,
	UpdateCustomerCheckCategoryRequest
} from '../models/be/Customer';
import User from '../models/be/coreApi/User';
import { LicenceCheckType, Licence } from '../models/be/coreApi/Licence';
import { createQueryString, getStartAndEndDate } from '../lib/utils';
import FileItem from '../models/be/coreApi/FileItem';
import ImageFile from '../models/ImageFile';
import ThirdPartyIntegration from '../models/be/coreApi/Integration';
import Provider from '../models/be/Providers';
import Branding, { UserModel } from '../models/be/Branding';
import ReportWorkflows from '../models/be/ReportWorkflows';
import { CreateBillingSurchargeRequest, LatestWorkflow, SurchargeBilling } from '../models/be/Workflow';
import Candidate from '../models/be/Candidate';
import { getCandidateIdFromAccessToken } from './session';
import ResetPassword from '../models/ResetPassword';

const appApi = new AppApi(coreApiRootUrl);
const fnAppApi = new AppApi(apiRootUrl);

export const getSubCustomer = async (subCustomerId: number): Promise<Customer> => {
	return await appApi.ajax('get', `/v1/customers/${subCustomerId}`);
};

export const alluserssecret = async (customerId: number, setSecret: number): Promise<void> => {
	await appApi.ajax('get', `/v1/users/alluserssecret/${customerId}/${setSecret}`);
};

export const getCustomerLastLogin = async (customerId: number, signal: AbortSignal = null): Promise<Customer[]> => {
	return await appApi.ajax('get', `/v1/customers/${customerId}/metrics`, null, signal);
};

export const getCustomerTree = async (customerId: number, signal: AbortSignal = null): Promise<Customer> => {
	const customer: Customer = await appApi.ajax('get', `/v1/customers/${customerId}`, null, signal);

	customer.childCustomer = customer.childCustomer
		?.filter((x) => x.customerName != null && x.customerId != null)
		.sort((a, b) => {
			return a.customerName?.toLowerCase().localeCompare(b.customerName?.toLowerCase());
		});

	return customer;
};

// export const createCustomer = async (customer: Customer): Promise<number> => {
// 	const { customerId } = await appApi.ajax('POST', '/api/Customers', customer);
// 	return customerId;
// };

export const getUsersByCustomerId = async (
	customerId: number,
	includeDisabled = true,
	signal: AbortSignal = null
): Promise<User[]> => {
	return await appApi.ajax(
		'get',
		`/v1/users?customerId=${customerId}&includeDisabled=${includeDisabled}`,
		null,
		signal
	);
};

export const getUserByEmail = async (email: string, signal: AbortSignal = null): Promise<User> => {
	return appApi.ajax('get', `/v1/users/email/${email}`, null, signal, false);
};

export const getCustomerUser = async (userId: number): Promise<User> => {
	return await appApi.ajax('get', `/v1/users/${userId}`);
};

export const getCustomerUserMFA = async (userId: number, accessToken: string): Promise<User> => {
	const customAuthorizationHeader = `Bearer ${accessToken}`;
	return await appApi.ajax('get', `/v1/users/${userId}`, null, null, false, null, customAuthorizationHeader);
};

export const getLicencesForUser = async (userId: number): Promise<Licence[]> => {
	return appApi.ajax('get', `/v1/licence/${userId}`);
};

export const setPermittedCheckTypesForUser = async (checkTypes: LicenceCheckType[]): Promise<void> => {
	return appApi.ajax('post', '/v1/licence', checkTypes);
};

export const getPublicDisplayName = async (customerId: number, accessToken: string) => {
	const customAuthorizationHeader = `Bearer ${accessToken}`;
	return await appApi.ajax(
		'get',
		`/v1/customers/${customerId}/publicDisplayName`,
		null,
		null,
		false,
		null,
		customAuthorizationHeader
	);
};

export const createConsentImage = async (imageFile: ImageFile): Promise<string> => {
	return await appApi.ajax('post', `/v1/files/image`, imageFile);
};

export const getConsentImage = async (imageName: string): Promise<FileItem> => {
	const queryString = createQueryString({ imageName });
	return await appApi.ajax('get', `/v1/files/image${queryString}`);
};

export const getIntegrations = async (): Promise<ThirdPartyIntegration[]> => {
	return await fnAppApi.ajax('get', `/api/integrations`);
};

export const getProviders = async (customerId: number): Promise<Provider[]> => {
	const queryString = createQueryString({ customerId });
	return await appApi.ajax('get', `/v1/providers${queryString}`);
};

export const getBranding = async (brandingName: string, signal: AbortSignal): Promise<Branding> => {
	return await appApi.ajax('get', `/v1/customers/Branding/${brandingName}`, null, signal, false);
};

export const createCustomerRegistration = async (customer: Registration): Promise<number> => {
	const { customerId } = await appApi.ajax('post', '/v1/Customers/register', customer, null, false);
	return customerId;
};

export const resetCustomerUserMFA = async (userId: number): Promise<unknown> => {
	return await appApi.ajax('post', `/v1/users/${userId}/mfa-reset`, null);
};

export const resetCustomerUserPassword = async (resetPasswordRequest: ResetPassword): Promise<unknown> => {
	return await appApi.ajax('post', `/v1/users/forgotten-password`, resetPasswordRequest);
};

export const getCoreWorkflows = async (
	selectedCustomerId: number,
	includeOutcome: boolean,
	timeperiod: string,
	checkStatus?: string,
	checkType?: string,
	signal?: AbortSignal
): Promise<ReportWorkflows[]> => {
	const dateRange = getStartAndEndDate(timeperiod);
	const data = await appApi.ajax(
		'get',
		`/v1/workflows/report?clientCustomerId=${selectedCustomerId}&fromDate=${dateRange.startDate}&toDate=${dateRange.endDate}&checkType=${checkType}&includeOutcome=${includeOutcome}&checkStatus=${checkStatus}`,
		null,
		signal
	);
	return data;
};

export const getCheckTypeCoreWorkflows = async (
	selectedCustomerId: number,
	includeOutcome: boolean,
	timeperiod: string,
	checkStatus?: string,
	checkType?: string,
	signal?: AbortSignal
): Promise<ReportWorkflows[]> => {
	const dateRange = getStartAndEndDate(timeperiod);
	const data = await appApi.ajax(
		'get',
		`/v1/workflows/report/check-type?clientCustomerId=${selectedCustomerId}&fromDate=${dateRange.startDate}&toDate=${dateRange.endDate}&checkType=${checkType}&includeOutcome=${includeOutcome}&checkStatus=${checkStatus}`,
		null,
		signal
	);
	return data;
};

export const getCheckStatusCoreWorkflows = async (
	selectedCustomerId: number,
	includeOutcome: boolean,
	timeperiod: string,
	checkStatus?: string,
	checkType?: string,
	signal?: AbortSignal
): Promise<ReportWorkflows[]> => {
	const dateRange = getStartAndEndDate(timeperiod);
	const data = await appApi.ajax(
		'get',
		`/v1/workflows/report/check-status?clientCustomerId=${selectedCustomerId}&fromDate=${dateRange.startDate}&toDate=${dateRange.endDate}&checkType=${checkType}&includeOutcome=${includeOutcome}&checkStatus=${checkStatus}`,
		null,
		signal
	);
	return data;
};

export const getCoreLatestWorkflow = async (
	selectedCustomerId: number,
	signal?: AbortSignal
): Promise<LatestWorkflow[]> =>
	appApi.ajax('get', `/v1/workflows/latest?currentCustomerId=${selectedCustomerId}`, null, signal);

export const getWorkflowSurcharges = async (
	workflowId: string,
	customerId: number,
	subCustomerId: number,
	signal?: AbortSignal
): Promise<SurchargeBilling[]> => {
	const queryString = createQueryString({ workflowId, customerId, subCustomerId });
	return appApi.ajax('get', `/v1/billings/surcharge${queryString}`, null, signal);
};

export const createWorkflowSurcharge = async (
	createBillingSurchargeRequest: CreateBillingSurchargeRequest,
	signal?: AbortSignal
): Promise<SurchargeBilling[]> => appApi.ajax('post', `/v1/billings/surcharge`, createBillingSurchargeRequest, signal);

export const deleteWorkflowSurcharge = async (
	workflowId: string,
	requestSurchargeID: number,
	signal?: AbortSignal
): Promise<SurchargeBilling[]> => {
	const queryString = createQueryString({ workflowId, requestSurchargeID });
	return appApi.ajax('delete', `/v1/billings/surcharge${queryString}`, signal);
};

export const getCustomerAddress = async (customerId: number, signal: AbortSignal): Promise<string> => {
	const qs = createQueryString({ customerId });
	return await appApi.ajax('get', `/v1/Customers/${customerId}/customerAddress${qs}`, null, signal);
};

export const createCustomer = async (customer: Customer): Promise<number> => {
	const { customerId } = await appApi.ajax('post', '/v1/Customers', customer);
	return customerId;
};

export const updateCustomer = async (customer: Customer): Promise<void> => {
	await appApi.ajax('put', `/v1/customers/${customer.customerId}`, customer);
	if (customer.twoFactorAuthenticationEnabled) {
		await alluserssecret(customer.customerId, 1);
	}
	if (!customer.twoFactorAuthenticationEnabled) {
		await alluserssecret(customer.customerId, 0);
	}
};

export const deleteCustomer = async (customer: Customer): Promise<void> => {
	await appApi.ajax('delete', `/v1/customers/${customer.customerId}`);
};

export const enableCustomer = async (customerId: number, enabled: boolean) => {
	const body = {
		customerId,
		deactivated: !enabled
	};
	await appApi.ajax('PATCH', `/v1/customers`, body);
};

export const updateCustomerBrandingProperties = async (id: number, brandingProperties: Branding): Promise<void> => {
	return await appApi.ajax('put', `/v1/customers/${id}/customerBrandingProperties`, brandingProperties);
};

export const createUser = async (user: User): Promise<UserModel> => {
	return appApi.ajax('post', '/v1/Users', user);
};

export const updateUser = async (user: User): Promise<UserModel> => {
	return appApi.ajax('put', `/v1/Users`, user);
};

export const deleteUser = async (id: number): Promise<UserModel> => {
	return appApi.ajax('delete', `/v1/Users/${id}`);
};

export const getCandidateId = async (
	externalCandidateId: string,
	integrationId: string,
	clientCustomerId: number = null,
	signal: AbortSignal = null
): Promise<string> => {
	const queryString = createQueryString({ externalCandidateId, clientCustomerId });
	return await appApi.ajax('get', `/v1/candidates/integration/${integrationId}${queryString}`, null, signal);
};

export const getCandidate = async (candidateId: string, signal: AbortSignal = null): Promise<Candidate> => {
	const queryString = candidateId === undefined ? getCandidateIdFromAccessToken() : `?candidateId=${candidateId}`;
	const responseString = await appApi.ajax(
		'get',
		`/v1/candidates/${candidateId ?? getCandidateIdFromAccessToken()}${queryString}`,
		null,
		signal
	);
	return JSON.parse(responseString) as Candidate;
};

export const archiveOrUnArchiveCandidates = async (candidateIds: string[], archive: boolean): Promise<void> => {
	return await appApi.ajax('put', `/v1/candidates/archive${createQueryString({ archive })}`, candidateIds);
};

// includeInherited resolves check types the customer does not define from its nearest ancestor.
// Only use it when consuming the categories - the management screen must show the customer's own
// rows, as inherited rows belong to another customer and cannot be edited or deleted there.
export const getCustomerCheckCategories = async (
	customerId: number,
	signal: AbortSignal = null,
	includeInherited = false
): Promise<CustomerCheckCategory[]> => {
	const response = await appApi.ajax(
		'get',
		`/v1/customercheckcategory/${customerId}${createQueryString({ includeInherited })}`,
		null,
		signal
	);
	return response;
};

export const createCustomerCheckCategory = async (
	data: CreateCustomerCheckCategoryRequest,
	signal: AbortSignal = null
): Promise<CustomerCheckCategory> => {
	const response = await appApi.ajax('post', '/v1/customercheckcategory', data, signal);
	return response;
};

export const updateCustomerCheckCategory = async (
	customerCheckCategoryId: string,
	data: UpdateCustomerCheckCategoryRequest,
	signal: AbortSignal = null
): Promise<CustomerCheckCategory> => {
	const response = await appApi.ajax('put', `/v1/customercheckcategory/${customerCheckCategoryId}`, data, signal);
	return response;
};

export const deleteCustomerCheckCategory = async (
	customerCheckCategoryId: string,
	signal: AbortSignal = null
): Promise<void> => {
	await appApi.ajax('delete', `/v1/customercheckcategory/${customerCheckCategoryId}`, null, signal);
};
